import React from "react";

// Translation strings come from Tolgee, whose ICU export rewrites plurals into
// its own normal form on every import: text around the block moves into each
// branch ("{count, plural, one {Held by {value} customer} other {...}}"),
// branches a language doesn't use are dropped (Turkish keeps only "other"),
// and a space can trail the last branch. So plural blocks are parsed with
// balanced braces instead of a regex, and the branch is picked by the
// language's plural rules.

const PLURAL_HEAD = /^\{\s*(\w+)\s*,\s*plural\s*,/;

// Returns the index just past the "}" matching the "{" at `open`, or -1.
function matchBrace(str, open) {
  let depth = 0;
  for (let i = open; i < str.length; i++) {
    if (str[i] === "{") depth++;
    else if (str[i] === "}") {
      depth--;
      if (depth === 0) return i + 1;
    }
  }
  return -1;
}

// Parses "one {a} other {b}" (the part after "plural,") into { one: "a", other: "b" }.
function parseBranches(body) {
  const branches = {};
  let i = 0;
  while (i < body.length) {
    while (i < body.length && /\s/.test(body[i])) i++;
    if (i >= body.length) break;
    const selector = body.slice(i).match(/^(=\d+|\w+)\s*\{/);
    if (!selector) return null;
    const open = i + selector[0].length - 1;
    const close = matchBrace(body, open);
    if (close === -1) return null;
    branches[selector[1]] = body.slice(open + 1, close - 1);
    i = close;
  }
  return branches.other === undefined ? null : branches;
}

function pickBranch(branches, count, locale) {
  const exact = branches[`=${count}`];
  if (exact !== undefined) return exact;
  let category = "other";
  try {
    category = new Intl.PluralRules(locale).select(Number(count));
  } catch {
    category = Number(count) === 1 ? "one" : "other";
  }
  return branches[category] ?? branches.other;
}

export function resolvePlurals(template, variables, locale) {
  let out = "";
  let i = 0;
  while (i < template.length) {
    const open = template.indexOf("{", i);
    if (open === -1) break;
    const head = template.slice(open).match(PLURAL_HEAD);
    const close = head ? matchBrace(template, open) : -1;
    const branches =
      close === -1 ? null : parseBranches(template.slice(open + head[0].length, close - 1));
    const count = head ? variables[head[1]] : undefined;
    if (!branches || count === undefined || count === null) {
      // Not a plural block, or no count to choose with: keep the text as is.
      const next = close === -1 ? open + 1 : close;
      out += template.slice(i, next);
      i = next;
      continue;
    }
    const chosen = pickBranch(branches, count, locale).replace(/#/g, String(count));
    out += template.slice(i, open) + resolvePlurals(chosen, variables, locale);
    i = close;
  }
  return out + template.slice(i);
}

// t()'s formatter: plurals first, then {name} placeholders. A React element as
// a variable makes the result an array so it can render inline.
export function formatMessage(template, variables, locale) {
  const processed = resolvePlurals(template, variables, locale);
  // Only bare {name} tokens are placeholders; anything else (an unresolved
  // plural block, say) stays text.
  const parts = processed.split(/(\{\w+\})/g);
  let hasJSX = false;

  const result = parts.map((part, index) => {
    const match = part.match(/^\{(\w+)\}$/);
    if (!match) return part;

    const replacement = variables[match[1]];
    if (replacement === undefined || replacement === null) return match[0];

    if (React.isValidElement(replacement)) {
      hasJSX = true;
      return React.cloneElement(replacement, { key: index });
    }
    return String(replacement);
  });

  return hasJSX ? result : result.join("");
}
